import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, Typography, Dialog, DialogTitle, DialogContent } from "@mui/material";
import {
  BsCloudSunFill,
  BsThermometerHalf,
  BsWind,
  BsDropletHalf,
  BsSpeedometer2,
  BsBrightnessHighFill,
  BsPlayFill,
  BsPauseFill,
  BsEyeFill,
  BsUpload,
  BsBookHalf,
  BsGeoAltFill,
  BsArrowUpRight,
  BsCheckCircleFill,
  BsStars,
} from "react-icons/bs";
import "./cloud_analysis.css";

import skyView1 from "./assets/sky_view_1.jpg";
import skyView2 from "./assets/sky_view_2.jpg";
import skyView3 from "./assets/sky_view_3.jpg";
import skyView4 from "./assets/sky_view_4.jpg";
import skyView5 from "./assets/sky_view_5.jpg";
import skyView6 from "./assets/sky_view_6.jpg";

import skyThumb1 from "./assets/sky_thumb_1.jpg";
import skyThumb2 from "./assets/sky_thumb_2.jpg";
import skyThumb3 from "./assets/sky_thumb_3.jpg";
import skyThumb4 from "./assets/sky_thumb_4.jpg";
import skyThumb5 from "./assets/sky_thumb_5.jpg";
import skyThumb6 from "./assets/sky_thumb_6.jpg";
import evolutionStripImg from "./assets/cloud_evolution_strip.jpg";

const STATIONS = [
  {
    id: "doha",
    name: "Qatar - Doha",
    stationCode: "Station 01",
    shortName: "Doha (Qatar)",
    coords: "25.2854° N, 51.5310° E",
    lat: 25.2854,
    lng: 51.531,
    elevation: "35 m",
    status: "Online",
    lastImage: "09:20",
    skyImage: skyView1,
    cloudCoverage: 42,
    dominantType: "Cumulus",
    dominantPct: 52,
    secondaryType: "Cirrus",
    secondaryPct: 18,
    skyCondition: "Partly Cloudy",
    movementDir: "NW → SE",
    movementAngle: 135,
    movementSpeed: "18 km/h",
    development: "Stable",
    sunObstruction: "Partial",
    visibility: "Good",
    confidence: 93,
    temp: "31.8°C",
    tempDelta: "1.2°C",
    windSpeed: "17 km/h",
    windDir: "NW",
    humidity: "61%",
    humidityDelta: "5%",
    pressure: "1007 hPa",
    pressureDelta: "2 hPa",
    solarRad: "610 W/m²",
    distribution: [
      { name: "Cumulus", pct: 52, color: "#0c7bff" },
      { name: "Cirrus", pct: 18, color: "#38bdf8" },
      { name: "Stratus", pct: 12, color: "#6366f1" },
      { name: "Altostratus", pct: 8, color: "#a855f7" },
      { name: "Others", pct: 10, color: "#94a3b8" },
    ],
    hourlyCoverage: [38, 44, 49, 51, 56, 48, 43, 47, 42],
  },
  {
    id: "dubai",
    name: "UAE - Dubai",
    stationCode: "Station 02",
    shortName: "Dubai (UAE)",
    coords: "25.2048° N, 55.2708° E",
    lat: 25.2048,
    lng: 55.2708,
    elevation: "16 m",
    status: "Online",
    lastImage: "09:20",
    skyImage: skyView2,
    cloudCoverage: 67,
    dominantType: "Altostratus",
    dominantPct: 46,
    secondaryType: "Cumulus",
    secondaryPct: 28,
    skyCondition: "Mostly Cloudy",
    movementDir: "W → E",
    movementAngle: 90,
    movementSpeed: "22 km/h",
    development: "Developing",
    sunObstruction: "Moderate",
    visibility: "Good",
    confidence: 95,
    temp: "33.4°C",
    tempDelta: "0.9°C",
    windSpeed: "21 km/h",
    windDir: "W",
    humidity: "68%",
    humidityDelta: "7%",
    pressure: "1005 hPa",
    pressureDelta: "1 hPa",
    solarRad: "480 W/m²",
    distribution: [
      { name: "Altostratus", pct: 46, color: "#0c7bff" },
      { name: "Cumulus", pct: 28, color: "#38bdf8" },
      { name: "Stratus", pct: 14, color: "#6366f1" },
      { name: "Cirrus", pct: 7, color: "#a855f7" },
      { name: "Others", pct: 5, color: "#94a3b8" },
    ],
    hourlyCoverage: [52, 58, 63, 70, 72, 69, 65, 68, 67],
  },
  {
    id: "riyadh",
    name: "KSA - Riyadh",
    stationCode: "Station 03",
    shortName: "Riyadh (KSA)",
    coords: "24.7136° N, 46.6753° E",
    lat: 24.7136,
    lng: 46.6753,
    elevation: "612 m",
    status: "Online",
    lastImage: "09:19",
    skyImage: skyView3,
    cloudCoverage: 31,
    dominantType: "Cirrus",
    dominantPct: 58,
    secondaryType: "Cumulus",
    secondaryPct: 24,
    skyCondition: "Scattered Clouds",
    movementDir: "SW → NE",
    movementAngle: 45,
    movementSpeed: "14 km/h",
    development: "Stable",
    sunObstruction: "Low",
    visibility: "Excellent",
    confidence: 96,
    temp: "34.1°C",
    tempDelta: "1.5°C",
    windSpeed: "14 km/h",
    windDir: "SW",
    humidity: "29%",
    humidityDelta: "2%",
    pressure: "1011 hPa",
    pressureDelta: "1 hPa",
    solarRad: "790 W/m²",
    distribution: [
      { name: "Cirrus", pct: 58, color: "#0c7bff" },
      { name: "Cumulus", pct: 24, color: "#38bdf8" },
      { name: "Altostratus", pct: 9, color: "#6366f1" },
      { name: "Stratus", pct: 5, color: "#a855f7" },
      { name: "Others", pct: 4, color: "#94a3b8" },
    ],
    hourlyCoverage: [22, 25, 29, 35, 38, 34, 30, 32, 31],
  },
  {
    id: "muscat",
    name: "Oman - Muscat",
    stationCode: "Station 04",
    shortName: "Muscat (Oman)",
    coords: "23.5859° N, 58.4059° E",
    lat: 23.5859,
    lng: 58.4059,
    elevation: "14 m",
    status: "Online",
    lastImage: "09:20",
    skyImage: skyView4,
    cloudCoverage: 58,
    dominantType: "Cumulus",
    dominantPct: 49,
    secondaryType: "Stratus",
    secondaryPct: 26,
    skyCondition: "Partly Cloudy",
    movementDir: "SE → NW",
    movementAngle: 315,
    movementSpeed: "19 km/h",
    development: "Developing",
    sunObstruction: "Partial",
    visibility: "Good",
    confidence: 92,
    temp: "30.9°C",
    tempDelta: "0.7°C",
    windSpeed: "19 km/h",
    windDir: "SE",
    humidity: "72%",
    humidityDelta: "6%",
    pressure: "1006 hPa",
    pressureDelta: "1 hPa",
    solarRad: "540 W/m²",
    distribution: [
      { name: "Cumulus", pct: 49, color: "#0c7bff" },
      { name: "Stratus", pct: 26, color: "#38bdf8" },
      { name: "Cirrus", pct: 13, color: "#6366f1" },
      { name: "Altostratus", pct: 7, color: "#a855f7" },
      { name: "Others", pct: 5, color: "#94a3b8" },
    ],
    hourlyCoverage: [45, 50, 54, 60, 62, 59, 55, 57, 58],
  },
  {
    id: "kuwait",
    name: "Kuwait - Kuwait City",
    stationCode: "Station 05",
    shortName: "Kuwait City (Kuwait)",
    coords: "29.3117° N, 47.4818° E",
    lat: 29.3117,
    lng: 47.4818,
    elevation: "22 m",
    status: "Online",
    lastImage: "09:18",
    skyImage: skyView5,
    cloudCoverage: 44,
    dominantType: "Cumulus",
    dominantPct: 50,
    secondaryType: "Cirrus",
    secondaryPct: 22,
    skyCondition: "Partly Cloudy",
    movementDir: "NW → SE",
    movementAngle: 135,
    movementSpeed: "16 km/h",
    development: "Stable",
    sunObstruction: "Partial",
    visibility: "Good",
    confidence: 94,
    temp: "32.1°C",
    tempDelta: "1.1°C",
    windSpeed: "16 km/h",
    windDir: "NW",
    humidity: "48%",
    humidityDelta: "3%",
    pressure: "1009 hPa",
    pressureDelta: "2 hPa",
    solarRad: "640 W/m²",
    distribution: [
      { name: "Cumulus", pct: 50, color: "#0c7bff" },
      { name: "Cirrus", pct: 22, color: "#38bdf8" },
      { name: "Stratus", pct: 15, color: "#6366f1" },
      { name: "Altostratus", pct: 8, color: "#a855f7" },
      { name: "Others", pct: 5, color: "#94a3b8" },
    ],
    hourlyCoverage: [36, 40, 46, 50, 48, 45, 42, 43, 44],
  },
  {
    id: "manama",
    name: "Bahrain - Manama",
    stationCode: "Station 06",
    shortName: "Manama (Bahrain)",
    coords: "26.2235° N, 50.5876° E",
    lat: 26.2235,
    lng: 50.5876,
    elevation: "11 m",
    status: "Online",
    lastImage: "09:20",
    skyImage: skyView6,
    cloudCoverage: 36,
    dominantType: "Cumulus",
    dominantPct: 54,
    secondaryType: "Cirrus",
    secondaryPct: 20,
    skyCondition: "Partly Cloudy",
    movementDir: "NW → SE",
    movementAngle: 135,
    movementSpeed: "15 km/h",
    development: "Clearing",
    sunObstruction: "Low",
    visibility: "Excellent",
    confidence: 94,
    temp: "31.4°C",
    tempDelta: "0.8°C",
    windSpeed: "15 km/h",
    windDir: "NW",
    humidity: "59%",
    humidityDelta: "4%",
    pressure: "1008 hPa",
    pressureDelta: "1 hPa",
    solarRad: "675 W/m²",
    distribution: [
      { name: "Cumulus", pct: 54, color: "#0c7bff" },
      { name: "Cirrus", pct: 20, color: "#38bdf8" },
      { name: "Stratus", pct: 11, color: "#6366f1" },
      { name: "Altostratus", pct: 9, color: "#a855f7" },
      { name: "Others", pct: 6, color: "#94a3b8" },
    ],
    hourlyCoverage: [30, 34, 39, 42, 41, 38, 35, 37, 36],
  },
];

const RECENT_HOURS = [
  { time: "09:20", coverage: 40, live: true, thumb: skyThumb1, fullImg: skyView1 },
  { time: "08:20", coverage: 45, live: false, thumb: skyThumb2, fullImg: skyView2 },
  { time: "07:20", coverage: 47, live: false, thumb: skyThumb3, fullImg: skyView3 },
  { time: "06:20", coverage: 35, live: false, thumb: skyThumb4, fullImg: skyView4 },
  { time: "05:20", coverage: 40, live: false, thumb: skyThumb5, fullImg: skyView5 },
  { time: "04:20", coverage: 25, live: false, thumb: skyThumb6, fullImg: skyView6 },
];

const CLOUD_ENCYCLOPEDIA = [
  {
    type: "Cumulus",
    altitude: "600 – 2,000 m (Low-Level)",
    appearance: "Puffy, cotton-like heaps with flat bases and bright white sunlit tops.",
    prediction: "Indicates fair weather and upward thermal convection; can grow into Cumulonimbus if vertical moisture increases.",
    solarImpact: "Causes intermittent rapid drops (30–60%) in solar irradiance as individual clouds pass the sun.",
  },
  {
    type: "Cirrus",
    altitude: "6,000 – 12,000 m (High-Level)",
    appearance: "Thin, wispy, hair-like ice-crystal strands high in the troposphere.",
    prediction: "Often precedes a warm front or weather change within 24–36 hours.",
    solarImpact: "Low solar attenuation (5–15%); diffuses sunlight slightly.",
  },
  {
    type: "Stratus",
    altitude: "0 – 1,200 m (Low-Level)",
    appearance: "Uniform, featureless grey cloud layer covering most of the sky like high fog.",
    prediction: "Associated with stable atmospheric layers, overcast skies, or light drizzle.",
    solarImpact: "High continuous solar attenuation (50–75%).",
  },
  {
    type: "Altostratus",
    altitude: "2,000 – 6,000 m (Mid-Level)",
    appearance: "Greyish or bluish-grey sheet partially revealing the sun as through ground glass.",
    prediction: "Frequently signals an approaching widespread frontal system within 12–24 hours.",
    solarImpact: "Moderate to heavy solar attenuation (40–65%).",
  },
];

/**
 * Pixel-Level Red-Blue Ratio ((B - R) / (B + R)) & Texture Sky Image Analyzer
 */
function analyzeSkyImage(imgElement) {
  const size = 180;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(imgElement, 0, 0, size, size);

  const imgData = ctx.getImageData(0, 0, size, size);
  const data = imgData.data;

  let skyPixels = 0;
  let cloudPixels = 0;
  let brightSunPixels = 0;
  let edgeSum = 0;
  let thickCloudPixels = 0;
  let thinCloudPixels = 0;

  const cx = size / 2;
  const cy = size / 2;
  const maxR = size * 0.45;

  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > maxR * maxR) continue;

      const idx = (y * size + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];

      const brightness = (r + g + b) / 3;
      if (brightness < 35) continue;

      skyPixels++;
      const nbr = (b - r) / (b + r + 1);

      if (brightness > 242 && Math.abs(r - b) < 18) {
        brightSunPixels++;
      }

      if (nbr < 0.09 && brightness > 180) {
        cloudPixels++;
        if (brightness > 215 && nbr < 0.05) {
          thickCloudPixels++;
        } else {
          thinCloudPixels++;
        }
      }

      const rightIdx = (y * size + (x + 1)) * 4;
      edgeSum += Math.abs(r - data[rightIdx]);
    }
  }

  const coverage = skyPixels > 0 ? Math.min(99, Math.max(1, Math.round((cloudPixels / skyPixels) * 100))) : 42;
  const avgEdge = skyPixels > 0 ? edgeSum / skyPixels : 8;

  let dominantType = "Cumulus";
  let secondaryType = "Cirrus";
  if (coverage < 20) {
    dominantType = "Cirrus";
    secondaryType = "Cumulus";
  } else if (thickCloudPixels > thinCloudPixels * 1.1 && avgEdge > 6) {
    dominantType = "Cumulus";
    secondaryType = "Cirrus";
  } else if (coverage > 70 && avgEdge < 5.5) {
    dominantType = "Stratus";
    secondaryType = "Altostratus";
  } else {
    dominantType = "Altostratus";
    secondaryType = "Cumulus";
  }

  const sunObstruction =
    brightSunPixels > 35 && coverage < 45
      ? "Clear"
      : coverage > 75
      ? "Obstructed"
      : "Partial";

  const skyCondition =
    coverage < 15
      ? "Clear Sky"
      : coverage < 55
      ? "Partly Cloudy"
      : coverage < 80
      ? "Mostly Cloudy"
      : "Overcast";

  return {
    cloudCoverage: coverage,
    dominantType,
    dominantPct: Math.min(78, Math.max(38, Math.round((thickCloudPixels / (cloudPixels + 1)) * 75) + 15)),
    secondaryType,
    secondaryPct: 22,
    skyCondition,
    sunObstruction,
    confidence: Math.min(98, 90 + (coverage % 7)),
  };
}

/**
 * Real Photographic 180° Fisheye Sky Viewer with Real-Time Pixel Cloud Segmentation Mask
 */
function RealFisheyeSkyViewer({ imageSrc, size = 290, showMask = false, onAnalyzed }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageSrc) return;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);

      if (onAnalyzed) {
        onAnalyzed(analyzeSkyImage(img));
      }

      if (showMask) {
        const imgData = ctx.getImageData(0, 0, size, size);
        const d = imgData.data;
        const cx = size / 2;
        const cy = size / 2;
        const maxR = size * 0.44;

        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            const dx = x - cx;
            const dy = y - cy;
            if (dx * dx + dy * dy > maxR * maxR) continue;

            const idx = (y * size + x) * 4;
            const r = d[idx];
            const g = d[idx + 1];
            const b = d[idx + 2];
            const brightness = (r + g + b) / 3;
            const nbr = (b - r) / (b + r + 1);

            // Highlight detected Cumulus cloud pixels from clouds-.jpg in cyan/blue AI segmentation overlay
            if (brightness > 180 && nbr < 0.09) {
              d[idx] = Math.round(r * 0.42 + 12 * 0.58);
              d[idx + 1] = Math.round(g * 0.42 + 123 * 0.58);
              d[idx + 2] = Math.round(b * 0.42 + 255 * 0.58);
            }
          }
        }
        ctx.putImageData(imgData, 0, 0);
      }
    };
    img.src = imageSrc;
  }, [imageSrc, size, showMask]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        display: "block",
        margin: "0 auto",
        borderRadius: "50%",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
      }}
    />
  );
}

/**
 * Interactive Stations Map using Google Maps
 */
function StationsMap({ stations, activeStationId, onSelectStation }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google || !window.google.maps) return;
      if (!mapInstance.current) {
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          zoom: 5,
          center: { lat: 25.3, lng: 51.5 },
          mapTypeId: "hybrid",
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
        });
      }

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = stations.map((st) => {
        const isSelected = st.id === activeStationId;
        const marker = new window.google.maps.Marker({
          position: { lat: st.lat, lng: st.lng },
          map: mapInstance.current,
          title: `${st.shortName} - Cloud: ${st.cloudCoverage}%`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isSelected ? 9 : 7,
            fillColor: isSelected ? "#ffca28" : "#10b981",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });
        marker.addListener("click", () => onSelectStation(st.id));
        return marker;
      });
    };

    if (window.google && window.google.maps) {
      initMap();
    } else {
      const existingScript = document.getElementById("google-maps");
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = "google-maps";
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=AIzaSyCXLaGIDZ5poKhVKHwf6oXXrGmpAVlUsfU";
        script.async = true;
        script.onload = initMap;
        document.head.appendChild(script);
      } else {
        existingScript.addEventListener("load", initMap);
      }
    }
  }, [stations, activeStationId, onSelectStation]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height: 176,
        borderRadius: 12,
        overflow: "hidden",
        background: "#dbeafe",
        flexShrink: 0,
      }}
    />
  );
}

export default function CloudAnalysis() {
  const [activeStationId, setActiveStationId] = useState("doha");
  const [chartPeriod, setChartPeriod] = useState("1D");
  const [selectedHourIdx, setSelectedHourIdx] = useState(0);
  const [showMask, setShowMask] = useState(false);
  const [customImageSrc, setCustomImageSrc] = useState(null);
  const [customAnalysis, setCustomAnalysis] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timelineVal, setTimelineVal] = useState(42);
  const [learnOpen, setLearnOpen] = useState(false);
  const [trainedCount, setTrainedCount] = useState(() => {
    return Number(localStorage.getItem("skyai_trained_samples") || 128);
  });
  const [selectedLearnType, setSelectedLearnType] = useState("Cumulus");
  const [trainSavedMsg, setTrainSavedMsg] = useState("");
  const fileInputRef = useRef(null);

  const baseStation = STATIONS.find((s) => s.id === activeStationId) || STATIONS[0];
  const activeHourObj = RECENT_HOURS[selectedHourIdx];

  const station = customAnalysis
    ? {
        ...baseStation,
        cloudCoverage: customAnalysis.cloudCoverage,
        dominantType: customAnalysis.dominantType,
        dominantPct: customAnalysis.dominantPct,
        secondaryType: customAnalysis.secondaryType,
        secondaryPct: customAnalysis.secondaryPct,
        skyCondition: customAnalysis.skyCondition,
        sunObstruction: customAnalysis.sunObstruction,
        confidence: customAnalysis.confidence,
      }
    : selectedHourIdx > 0
    ? {
        ...baseStation,
        cloudCoverage: activeHourObj.coverage,
      }
    : baseStation;

  const activeSkyPhoto =
    customImageSrc ||
    (selectedHourIdx > 0 ? activeHourObj.fullImg : baseStation.skyImage);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setTimelineVal((prev) => {
        const next = prev >= 100 ? 0 : prev + 5;
        const hourIdx = Math.min(5, Math.floor(((100 - next) / 100) * 6));
        setSelectedHourIdx(hourIdx);
        return next;
      });
    }, 500);
    return () => clearInterval(timer);
  }, [isPlaying]);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      setCustomImageSrc(src);
      const img = new Image();
      img.onload = () => {
        const result = analyzeSkyImage(img);
        setCustomAnalysis(result);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const handleSaveTrainingSample = (cloudType) => {
    const next = trainedCount + 1;
    setTrainedCount(next);
    localStorage.setItem("skyai_trained_samples", String(next));
    setTrainSavedMsg(`Saved "${cloudType}" sample to AI model! Total learned samples: ${next}`);
    setTimeout(() => setTrainSavedMsg(""), 3500);
  };

  const timeLabels = ["06:00", "09:00", "12:00", "15:00", "18:00", "21:00", "00:00", "03:00", "06:00"];
  const chartPoints =
    chartPeriod === "1D"
      ? station.hourlyCoverage
      : chartPeriod === "7D"
      ? station.hourlyCoverage.map((v, i) => Math.max(15, Math.min(90, v + ((i % 3) - 1) * 8)))
      : station.hourlyCoverage.map((v, i) => Math.max(18, Math.min(88, v + (i % 2 ? 6 : -5))));

  const donutRadius = 52;
  const donutCircumference = 2 * Math.PI * donutRadius;
  let donutOffset = 0;

  return (
    <Box className="cloud-page-wrap">
      {/* Top Page Title & Action Header */}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
        gap={2}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.2}>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              Cloud Monitoring Network
            </Typography>
            <span className="cloud-pill-online">
              <BsStars size={13} /> SkyAI™ Cloud Observation
            </span>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: "#6c7280", mt: 0.3 }}>
            Real-time sky observation &nbsp;|&nbsp; AI cloud analysis &nbsp;|&nbsp; Environmental data &nbsp;|&nbsp; Long-term insights
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
          <button
            type="button"
            className="cloud-action-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            <BsUpload size={14} /> Analyze Sky Image
          </button>
          {customImageSrc && (
            <button
              type="button"
              className="cloud-action-btn"
              onClick={() => {
                setCustomImageSrc(null);
                setCustomAnalysis(null);
              }}
            >
              Reset Live Camera
            </button>
          )}
          <button
            type="button"
            className="cloud-action-btn primary"
            onClick={() => setLearnOpen(true)}
          >
            <BsBookHalf size={14} /> Cloud Learning & Prediction Lab ({trainedCount})
          </button>
        </Box>
      </Box>

      {/* ROW 1: 4 Equal-Height KPI Summary Cards */}
      <Grid container spacing={2.5} mb={2.5} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box className="cloud-kpi-card">
            <Box className="cloud-kpi-badge">12</Box>
            <Box className="cloud-kpi-info">
              <div className="cloud-kpi-title">Total Stations</div>
              <div className="cloud-kpi-sub">
                <span style={{ color: "#10b981", fontWeight: 600 }}>9 Online</span>
                <span style={{ color: "#94a3b8" }}>3 Offline</span>
              </div>
            </Box>
            <Box className="cloud-kpi-icon">
              <BsGeoAltFill size={20} />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box className="cloud-kpi-card">
            <Box className="cloud-kpi-badge">{station.cloudCoverage}%</Box>
            <Box className="cloud-kpi-info">
              <div className="cloud-kpi-title">Avg. Cloud Coverage</div>
              <div className="cloud-kpi-sub" style={{ color: "#10b981" }}>
                ↑ 6% <span style={{ color: "#94a3b8", fontWeight: 400 }}>vs. last 24h</span>
              </div>
            </Box>
            <Box className="cloud-kpi-icon">
              <BsCloudSunFill size={22} />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box className="cloud-kpi-card">
            <Box className="cloud-kpi-badge" style={{ fontSize: 15 }}>
              {station.dominantType}
            </Box>
            <Box className="cloud-kpi-info">
              <div className="cloud-kpi-title">Dominant Cloud Type</div>
              <div className="cloud-kpi-sub" style={{ color: "#64748b" }}>
                (Network average)
              </div>
            </Box>
            <Box className="cloud-kpi-icon">
              <BsCloudSunFill size={22} style={{ color: "#0c7bff" }} />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box className="cloud-kpi-card">
            <Box className="cloud-kpi-badge">32.4°C</Box>
            <Box className="cloud-kpi-info">
              <div className="cloud-kpi-title">Avg. Temperature</div>
              <div className="cloud-kpi-sub" style={{ color: "#10b981" }}>
                ↑ 1.2°C <span style={{ color: "#94a3b8", fontWeight: 400 }}>vs. last 24h</span>
              </div>
            </Box>
            <Box className="cloud-kpi-icon">
              <BsThermometerHalf size={22} style={{ color: "#f59e0b" }} />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* ROW 2: Main Station 180° Fisheye + AI Cloud Analysis (Left) & Stations Map + Table (Right) */}
      <Grid container spacing={2.5} mb={2.5} alignItems="stretch" className="cloud-row-main">
        {/* Left: Station Live Observation & AI Cloud Analysis */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box className="cloud-card">
            {/* Station Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              pb={1.2}
              borderBottom="1px solid #eef1f6"
              gap={1}
            >
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: "#10b981",
                      display: "inline-block",
                    }}
                  />
                  <Typography sx={{ fontSize: 16.5, fontWeight: 700 }}>
                    {station.name}{" "}
                    <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 13.5 }}>
                      ({station.stationCode})
                    </span>
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11.5, color: "#6c7280", mt: 0.2, ml: 2.4 }}>
                  {station.coords} &nbsp;|&nbsp; Elevation: {station.elevation}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1.2}>
                <button
                  type="button"
                  className={`cloud-tab-btn ${showMask ? "active" : ""}`}
                  onClick={() => setShowMask(!showMask)}
                >
                  {showMask ? "AI Cloud Mask: ON" : "AI Cloud Mask: OFF"}
                </button>
                <span className="cloud-pill-online">● {station.status}</span>
                <Typography sx={{ fontSize: 12, color: "#6c7280" }}>
                  Last image: <strong>{RECENT_HOURS[selectedHourIdx]?.time || station.lastImage}</strong>
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2} alignItems="center" sx={{ flex: 1, mt: 0.5 }}>
              {/* Real 180° Fisheye Sky Camera View */}
              <Grid size={{ xs: 12, md: 5.5 }}>
                <Box
                  sx={{
                    position: "relative",
                    background: "#0b111e",
                    borderRadius: "16px",
                    p: 1.5,
                    textAlign: "center",
                  }}
                >
                  <RealFisheyeSkyViewer
                    imageSrc={activeSkyPhoto}
                    size={282}
                    showMask={showMask}
                    onAnalyzed={(res) => setCustomAnalysis(res)}
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                    px={0.5}
                  >
                    <Box textAlign="left">
                      <Typography sx={{ fontSize: 11.5, color: "#e2e8f0", fontWeight: 600 }}>
                        ● Live / Latest Image
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                        24 Apr 2025 {RECENT_HOURS[selectedHourIdx]?.time || "09:20"} (GST)
                      </Typography>
                    </Box>
                    <span
                      style={{
                        background: "rgba(255,255,255,0.14)",
                        color: "#f8fafc",
                        fontSize: 11,
                        padding: "3px 10px",
                        borderRadius: 8,
                        fontWeight: 600,
                      }}
                    >
                      180° Fisheye
                    </span>
                  </Box>
                </Box>
              </Grid>

              {/* AI Cloud Analysis Sub-Panel */}
              <Grid size={{ xs: 12, md: 6.5 }}>
                <Box className="cloud-subcard" sx={{ p: 1.8 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                      <BsStars style={{ color: "#0c7bff" }} /> AI Cloud Analysis
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#059669", fontWeight: 600 }}>
                      AI Confidence: {station.confidence}%
                    </Typography>
                  </Box>

                  {/* Circular Gauge + Dominant / Secondary Cloud Types */}
                  <Grid container spacing={1.5} alignItems="center" mb={1.5}>
                    <Grid size={{ xs: 5 }} textAlign="center">
                      <Box sx={{ position: "relative", display: "inline-block" }}>
                        <svg width="106" height="106" viewBox="0 0 108 108">
                          <circle
                            cx="54"
                            cy="54"
                            r="44"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="9"
                          />
                          <circle
                            cx="54"
                            cy="54"
                            r="44"
                            fill="none"
                            stroke="#0c7bff"
                            strokeWidth="9"
                            strokeLinecap="round"
                            strokeDasharray={`${(station.cloudCoverage / 100) * 276.46} 276.46`}
                            transform="rotate(-90 54 54)"
                          />
                        </svg>
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>
                            {station.cloudCoverage}%
                          </Typography>
                          <Typography sx={{ fontSize: 9.5, color: "#6c7280" }}>
                            Cloud Coverage
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 7 }}>
                      <Box
                        sx={{
                          background: "#ffffff",
                          border: "1px solid #e6eaf2",
                          borderRadius: "10px",
                          p: 0.9,
                          mb: 0.8,
                        }}
                      >
                        <Typography sx={{ fontSize: 10.5, color: "#6c7280" }}>
                          Dominant Cloud Type
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0c7bff" }}>
                          ☁ {station.dominantType}{" "}
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                            ({station.dominantPct}%)
                          </span>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          background: "#ffffff",
                          border: "1px solid #e6eaf2",
                          borderRadius: "10px",
                          p: 0.9,
                        }}
                      >
                        <Typography sx={{ fontSize: 10.5, color: "#6c7280" }}>
                          Secondary Cloud Type
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          ☁ {station.secondaryType}{" "}
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                            ({station.secondaryPct}%)
                          </span>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* 6 AI Metric Cards */}
                  <Grid container spacing={1}>
                    {[
                      { label: "Sky Condition", val: station.skyCondition, icon: <BsCloudSunFill color="#f59e0b" /> },
                      { label: "Cloud Movement", val: station.movementDir, icon: <BsArrowUpRight color="#0c7bff" /> },
                      { label: "Movement Speed", val: station.movementSpeed, icon: <BsWind color="#0ea5e9" /> },
                      { label: "Cloud Development", val: station.development, icon: <BsCheckCircleFill color="#10b981" /> },
                      { label: "Sun Obstruction", val: station.sunObstruction, icon: <BsBrightnessHighFill color="#eab308" /> },
                      { label: "Visibility", val: station.visibility, icon: <BsEyeFill color="#6366f1" /> },
                    ].map((m, idx) => (
                      <Grid key={idx} size={{ xs: 6, sm: 4 }}>
                        <Box
                          sx={{
                            background: "#ffffff",
                            border: "1px solid #eaedf5",
                            borderRadius: "10px",
                            p: 0.9,
                          }}
                        >
                          <Typography sx={{ fontSize: 10, color: "#6c7280" }}>
                            {m.label}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.6} mt={0.2}>
                            {m.icon}
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                              {m.val}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Right: Stations Map & Interactive Station Table */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Box className="cloud-card">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                Stations Map
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ fontSize: 11.5 }}>
                <span>
                  <span style={{ color: "#10b981" }}>●</span> Online
                </span>
                <span>
                  <span style={{ color: "#ef4444" }}>●</span> Offline
                </span>
                <span>
                  <span style={{ color: "#f59e0b" }}>●</span> Maintenance
                </span>
              </Box>
            </Box>

            <StationsMap
              stations={STATIONS}
              activeStationId={activeStationId}
              onSelectStation={(id) => {
                setCustomImageSrc(null);
                setCustomAnalysis(null);
                setSelectedHourIdx(0);
                setActiveStationId(id);
              }}
            />

            {/* Station Selector Table */}
            <Box mt={1.2} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.45fr 1.55fr 1.05fr 0.85fr",
                  columnGap: "10px",
                  px: 1.5,
                  py: 0.8,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#6c7280",
                  borderBottom: "1px solid #e6eaf2",
                  whiteSpace: "nowrap",
                }}
              >
                <span>Station</span>
                <span>Location</span>
                <span>Cloud Coverage</span>
                <span>Status</span>
              </Box>
              <div className="cloud-station-scroll">
                {STATIONS.map((st) => (
                  <div
                    key={st.id}
                    className={`cloud-station-row ${st.id === activeStationId ? "active" : ""}`}
                    onClick={() => {
                      setCustomImageSrc(null);
                      setCustomAnalysis(null);
                      setSelectedHourIdx(0);
                      setActiveStationId(st.id);
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "#10b981" }}>●</span> {st.shortName}
                    </span>
                    <span style={{ color: "#64748b", fontSize: 11 }}>{st.coords}</span>
                    <span style={{ fontWeight: 700 }}>{st.cloudCoverage}%</span>
                    <span style={{ color: "#10b981", fontWeight: 600 }}>● {st.status}</span>
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* ROW 3: 3 Equal-Height Analytics & Environmental Cards */}
      <Grid container spacing={2.5} mb={2.5} alignItems="stretch" className="cloud-row-mid">
        {/* Cloud Coverage (Last 24 Hours) */}
        <Grid size={{ xs: 12, md: 4.5 }}>
          <Box className="cloud-card">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                Cloud Coverage{" "}
                <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 12 }}>
                  (Last 24 Hours)
                </span>
              </Typography>
              <Box display="flex" gap={0.6}>
                {["1D", "7D", "30D"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`cloud-tab-btn ${chartPeriod === p ? "active" : ""}`}
                    onClick={() => setChartPeriod(p)}
                  >
                    {p}
                  </button>
                ))}
              </Box>
            </Box>

            <svg width="100%" height="182" viewBox="0 0 420 170">
              <defs>
                <linearGradient id="cloudAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0c7bff" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#0c7bff" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {[0, 25, 50, 75, 100].map((pct) => {
                const y = 136 - (pct / 100) * 110;
                return (
                  <g key={pct}>
                    <line x1="36" y1={y} x2="405" y2={y} stroke="#eef1f6" strokeWidth="1" />
                    <text x="4" y={y + 4} fontSize="10" fill="#94a3b8">
                      {pct}%
                    </text>
                  </g>
                );
              })}

              {(() => {
                const pts = chartPoints.map((val, i) => {
                  const x = 44 + i * 44;
                  const y = 136 - (val / 100) * 110;
                  return [x, y, val];
                });
                const polyLine = pts.map((p) => `${p[0]},${p[1]}`).join(" ");
                const areaPath = `M ${pts[0][0]},136 L ${pts.map((p) => `${p[0]},${p[1]}`).join(" L ")} L ${pts[pts.length - 1][0]},136 Z`;

                return (
                  <>
                    <path d={areaPath} fill="url(#cloudAreaGrad)" />
                    <polyline
                      fill="none"
                      stroke="#0c7bff"
                      strokeWidth="2.5"
                      points={polyLine}
                    />
                    {pts.map(([x, y, val], i) => (
                      <g key={i}>
                        <circle cx={x} cy={y} r="4" fill="#ffffff" stroke="#0c7bff" strokeWidth="2.5" />
                        <text x={x - 13} y="158" fontSize="9.5" fill="#64748b">
                          {timeLabels[i]}
                        </text>
                        {i === 2 && (
                          <g>
                            <rect x={x - 52} y={y - 33} width="104" height="23" rx="6" fill="#1e293b" />
                            <text x={x - 44} y={y - 18} fontSize="9.5" fill="#ffffff" fontWeight="600">
                              12:00 Coverage: {val}%
                            </text>
                          </g>
                        )}
                      </g>
                    ))}
                  </>
                );
              })()}
            </svg>
          </Box>
        </Grid>

        {/* Cloud Type Distribution (Today) Donut Chart */}
        <Grid size={{ xs: 12, md: 3.5 }}>
          <Box className="cloud-card">
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
              Cloud Type Distribution{" "}
              <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 12 }}>
                (Today)
              </span>
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} sx={{ flex: 1 }}>
              <Box sx={{ position: "relative", width: 136, height: 136, flexShrink: 0 }}>
                <svg width="136" height="136" viewBox="0 0 136 136">
                  {station.distribution.map((seg, idx) => {
                    const dash = (seg.pct / 100) * donutCircumference;
                    const strokeDashoffset = -donutOffset;
                    donutOffset += dash;
                    return (
                      <circle
                        key={idx}
                        cx="68"
                        cy="68"
                        r={donutRadius}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="15"
                        strokeDasharray={`${dash} ${donutCircumference}`}
                        strokeDashoffset={strokeDashoffset}
                        transform="rotate(-90 68 68)"
                      />
                    );
                  })}
                </svg>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 19, fontWeight: 700 }}>
                    {station.cloudCoverage}%
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: "#6c7280" }}>
                    Total Cloud Cover
                  </Typography>
                </Box>
              </Box>

              <Box flex={1}>
                {station.distribution.map((seg, idx) => (
                  <Box
                    key={idx}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.8}
                    sx={{ fontSize: 12 }}
                  >
                    <Box display="flex" alignItems="center" gap={0.8}>
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: seg.color,
                          display: "inline-block",
                        }}
                      />
                      <span>{seg.name}</span>
                    </Box>
                    <strong style={{ fontWeight: 700 }}>{seg.pct}%</strong>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Environmental Data (6 Cards) */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box className="cloud-card">
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
              Environmental Data
            </Typography>
            <Grid container spacing={1} sx={{ flex: 1 }} alignItems="center">
              {[
                {
                  label: "Temperature",
                  val: station.temp,
                  delta: `↑ ${station.tempDelta}`,
                  icon: <BsThermometerHalf size={17} color="#ef4444" />,
                },
                {
                  label: "Wind Speed",
                  val: station.windSpeed,
                  delta: `↘ ${station.windDir}`,
                  icon: <BsWind size={17} color="#0ea5e9" />,
                },
                {
                  label: "Humidity",
                  val: station.humidity,
                  delta: `↑ ${station.humidityDelta}`,
                  icon: <BsDropletHalf size={17} color="#3b82f6" />,
                },
                {
                  label: "Wind Direction",
                  val: station.windDir,
                  delta: "Steady",
                  icon: <BsArrowUpRight size={17} color="#6366f1" />,
                },
                {
                  label: "Pressure",
                  val: station.pressure,
                  delta: `↑ ${station.pressureDelta}`,
                  icon: <BsSpeedometer2 size={17} color="#10b981" />,
                },
                {
                  label: "Solar Radiation",
                  val: station.solarRad,
                  delta: "Active",
                  icon: <BsBrightnessHighFill size={17} color="#f59e0b" />,
                },
              ].map((env, idx) => (
                <Grid key={idx} size={{ xs: 6 }}>
                  <Box className="cloud-subcard" display="flex" alignItems="center" gap={1}>
                    <Box>{env.icon}</Box>
                    <Box flex={1}>
                      <Typography sx={{ fontSize: 10, color: "#6c7280" }}>
                        {env.label}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="baseline">
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                          {env.val}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#10b981", fontWeight: 600 }}>
                          {env.delta}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* ROW 4: 3 Equal-Height Bottom Cards (Recent Sky Images + 24-Hour Cloud Evolution + Cloud Movement) */}
      <Grid container spacing={2.5} alignItems="stretch" className="cloud-row-bottom">
        {/* Recent Sky Images (Last 6 Hours) with Real Fisheye Photographs */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box className="cloud-card">
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.8 }}>
              Recent Sky Images{" "}
              <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 12 }}>
                ({station.shortName.split(" ")[0]} - Last 6 Hours)
              </span>
            </Typography>
            <Grid container spacing={0.8} alignItems="center" sx={{ flex: 1 }}>
              {RECENT_HOURS.map((item, idx) => (
                <Grid key={idx} size={{ xs: 4, sm: 2 }}>
                  <div
                    className={`fisheye-thumb ${selectedHourIdx === idx ? "active" : ""}`}
                    onClick={() => {
                      setCustomImageSrc(null);
                      setCustomAnalysis(null);
                      setSelectedHourIdx(idx);
                    }}
                  >
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={item.thumb}
                        alt={`Sky ${item.time}`}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          objectFit: "cover",
                          display: "block",
                          border: "1.5px solid #334155",
                        }}
                      />
                      {item.live && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: -2,
                            right: -3,
                            background: "#10b981",
                            color: "#fff",
                            fontSize: 8.5,
                            fontWeight: 700,
                            padding: "1px 5px",
                            borderRadius: 6,
                          }}
                        >
                          Live
                        </span>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, mt: 0.4 }}>
                      {item.time}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: "#6c7280" }}>
                      Cloud: <strong>{item.coverage}%</strong>
                    </Typography>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* 24-Hour Cloud Evolution Player with Real Photographic Strip */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box className="cloud-card">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>
                24-Hour Cloud Evolution{" "}
                <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 12 }}>
                  ({station.shortName.split(" ")[0]})
                </span>
              </Typography>
            </Box>

            <Box
              sx={{
                position: "relative",
                borderRadius: "10px",
                overflow: "hidden",
                height: 60,
                border: "1px solid #e2e8f0",
              }}
            >
              <img
                src={evolutionStripImg}
                alt="24-Hour Cloud Evolution"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${timelineVal}%`,
                  width: 3,
                  background: "#ffca28",
                  boxShadow: "0 0 6px rgba(0,0,0,0.6)",
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} mt={1.2}>
              <button
                type="button"
                className="cloud-play-btn"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <BsPauseFill size={16} /> : <BsPlayFill size={16} />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={timelineVal}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setTimelineVal(v);
                  const hourIdx = Math.min(5, Math.floor(((100 - v) / 100) * 6));
                  setSelectedHourIdx(hourIdx);
                }}
                style={{ flex: 1, accentColor: "#0c7bff", cursor: "pointer" }}
              />
              <Typography sx={{ fontSize: 11, color: "#64748b", minWidth: 34 }}>
                24:00
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Cloud Movement (Last 3 Hours) Compass Card */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Box className="cloud-card">
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5 }}>
              Cloud Movement{" "}
              <span style={{ fontWeight: 400, color: "#6c7280", fontSize: 11.5 }}>
                (Last 3 Hours)
              </span>
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.2} sx={{ flex: 1 }}>
              <svg width="92" height="92" viewBox="0 0 96 96">
                <circle
                  cx="48"
                  cy="48"
                  r="38"
                  fill="#f8fafc"
                  stroke="#cbd5e1"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
                <line x1="48" y1="12" x2="48" y2="84" stroke="#e2e8f0" strokeWidth="1" />
                <line x1="12" y1="48" x2="84" y2="48" stroke="#e2e8f0" strokeWidth="1" />
                <text x="44" y="10" fontSize="9" fontWeight="700" fill="#64748b">N</text>
                <text x="86" y="51" fontSize="9" fontWeight="700" fill="#64748b">E</text>
                <text x="45" y="94" fontSize="9" fontWeight="700" fill="#64748b">S</text>
                <text x="2" y="51" fontSize="9" fontWeight="700" fill="#64748b">W</text>
                <g transform={`rotate(${station.movementAngle} 48 48)`}>
                  <line x1="48" y1="66" x2="48" y2="22" stroke="#0c7bff" strokeWidth="3" strokeLinecap="round" />
                  <polygon points="48,17 42,27 54,27" fill="#0c7bff" />
                </g>
                <circle cx="48" cy="48" r="4" fill="#0c7bff" />
              </svg>

              <Box flex={1}>
                <Box display="flex" justifyContent="space-between" mb={0.6}>
                  <div>
                    <Typography sx={{ fontSize: 10, color: "#6c7280" }}>Direction</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{station.movementDir}</Typography>
                  </div>
                  <div>
                    <Typography sx={{ fontSize: 10, color: "#6c7280" }}>Speed</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{station.movementSpeed}</Typography>
                  </div>
                </Box>
                <Box className="cloud-subcard" sx={{ p: 0.7 }}>
                  <Typography sx={{ fontSize: 9.5, color: "#6c7280" }}>Movement Trend</Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#10b981" }}>
                    ● {station.development}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Interactive Cloud Learning & Prediction Lab Dialog */}
      <Dialog open={learnOpen} onClose={() => setLearnOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>☁ SkyAI™ Cloud Study, Prediction & Learning Lab</span>
          <span className="cloud-pill-online">Live AI Vision Mode</span>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: "#475569", mb: 2 }}>
            Learn cloud formations, inspect 3-hour movement predictions, and label sky samples to train the AI cloud classifier.
          </Typography>

          {/* 3-Hour Prediction Banner */}
          <Box className="cloud-subcard" sx={{ mb: 2.5, p: 2, borderLeft: "4px solid #0c7bff" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
              🔮 Next 3-Hour Cloud & Solar Prediction ({station.shortName})
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#334155" }}>
              Based on cloud movement vector <strong>{station.movementDir} ({station.movementSpeed})</strong> and dominant <strong>{station.dominantType}</strong> formations:
              Expected cloud coverage in +1h is <strong>{Math.min(95, station.cloudCoverage + 4)}%</strong>, +2h is <strong>{Math.max(10, station.cloudCoverage - 3)}%</strong>.
              Solar irradiance forecast: <strong>{station.solarRad}</strong> ({station.sunObstruction} sun obstruction).
            </Typography>
          </Box>

          {/* Cloud Encyclopedia & Interactive Labeling */}
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1.2 }}>
            📚 Cloud Classification Guide & AI Trainer
          </Typography>
          <Grid container spacing={1.5} mb={2}>
            {CLOUD_ENCYCLOPEDIA.map((c) => (
              <Grid key={c.type} size={{ xs: 12, sm: 6 }}>
                <Box
                  onClick={() => setSelectedLearnType(c.type)}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    border: selectedLearnType === c.type ? "2px solid #0c7bff" : "1px solid #e2e8f0",
                    background: selectedLearnType === c.type ? "#f0f7ff" : "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0c7bff" }}>
                      ☁ {c.type}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                      {c.altitude}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#334155", mt: 0.6 }}>
                    <strong>Structure:</strong> {c.appearance}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#334155", mt: 0.4 }}>
                    <strong>Weather Prediction:</strong> {c.prediction}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "#059669", mt: 0.4 }}>
                    <strong>Solar Impact:</strong> {c.solarImpact}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <button
                type="button"
                className="cloud-action-btn primary"
                onClick={() => handleSaveTrainingSample(selectedLearnType)}
              >
                + Label Current Sky as "{selectedLearnType}" & Train AI Model
              </button>
              <button
                type="button"
                className="cloud-action-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <BsUpload size={13} /> Upload New Sky Sample
              </button>
            </Box>
            {trainSavedMsg && (
              <Typography sx={{ fontSize: 12.5, color: "#059669", fontWeight: 600 }}>
                ✅ {trainSavedMsg}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
